import React, { useEffect, useRef } from 'react'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'

import { useIsSelectedNavigationListItem } from './hooks/useIsSelectedNavigationListItem'
import { useNavigationListKeyboardEventHandler } from './hooks/useNavigationListKeyboardEventHandler'
import { useNavigationListContext } from './navigationListContext'

function errorKeyboardEventHandler() {
  console.warn(
    `onKeyDown of NavigationListItem was used with "focusItems" set to false. This a no-op as the item will not get the focus to handle further navigation`
  )
}

/**
 * This component is here to simplify the usages of the NavigationList component
 * if you don't/can't use hooks.
 *
 * If you want to have the keyboard navigation handled on the item level, you
 * need to set `focusItems` to true on the NavigationList otherwise the keyboard
 * events won't fire as the focus will not be on the item itself.
 */
export const NavigationListItem = <T extends HTMLElement = HTMLElement>({
  id,
  selectOnHover = false,
  scrollIntoViewOnSelect = true,
  children: render,
}: {
  id: string
  selectOnHover?: boolean
  scrollIntoViewOnSelect?: boolean
  children: (
    props: {
      isSelected: boolean
      onKeyDown: React.KeyboardEventHandler
      onMouseEnter: React.MouseEventHandler
    },
    ref: React.Ref<T>
  ) => React.ReactNode
}) => {
  const ref = useRef<T | null>(null)
  const { focusItems, select, scrollBoundaryRef } = useNavigationListContext()

  const isSelected = useIsSelectedNavigationListItem(id, (source) => {
    if (!ref.current) {
      console.warn(
        'NavigationListItem ref is not set, this will prevent focusing and scrolling behaviours to be handled correctly.'
      )
      return
    }
    // Do not scroll into view if the item is hovered otherwise it will confuse
    // the user as the list will jump while trying to click on an item.
    if (scrollIntoViewOnSelect && source !== 'mouse') {
      scrollIntoViewIfNeeded(ref.current, {
        boundary: scrollBoundaryRef.current,
        behavior: 'smooth',
      })
    }
    if (focusItems) {
      ref.current.focus()
    }
  })

  const isInitiallySelectedRef = useRef(isSelected)
  useEffect(() => {
    if (isInitiallySelectedRef.current && focusItems && ref.current) {
      ref.current.focus()
    }
  }, [focusItems])

  const onKeyDown = useNavigationListKeyboardEventHandler()
  const onMouseEnter = () => {
    if (selectOnHover) {
      select(id, 'mouse')
    }
  }

  return (
    <>
      {render(
        {
          isSelected,
          onKeyDown: focusItems ? onKeyDown : errorKeyboardEventHandler,
          onMouseEnter,
        },
        ref
      )}
    </>
  )
}
